import { personalizeDoc, substitute } from '@mado/schema/personalize';

export { personalizeDoc, substitute };

/**
 * Personalisation: viewer-facing text can carry `{key}` or `{key|fallback}`
 * placeholders — "Welcome, {company}!", "{company|your team}" — filled from
 * the share URL's query string or, for an embed, the loader snippet's
 * `data-vars` (see loader.ts and apps/web's `/d/:slug` + `/e/:slug` routes,
 * which sanitize the query and forward it as this element's `vars`
 * attribute).
 *
 * No schema change: a placeholder is just text inside an existing string
 * field. The substitution itself lives in `@mado/schema/personalize` (the MP4
 * exporter uses the same code with no values, so a video shows fallbacks);
 * the player runs it once, at render time — every other
 * module (chrome, overlays, the announce() live region) already reads
 * `doc.title` / `step.name` / `step.alt` / annotation text / hotspot labels,
 * so replacing the document once in `Player`'s constructor is enough to reach
 * all of them.
 *
 * Values are plain text only (never HTML — every call site here uses
 * `textContent`, not `innerHTML`), length-capped, and never sent anywhere:
 * they do not reach `analytics.ts`, and nothing about them is persisted.
 */

/** Keys and values are capped so a malicious host page cannot use this as a
 *  way to inflate a URL or the DOM with unbounded text. */
const MAX_KEY_LEN = 32;
const MAX_VALUE_LEN = 60;
const MAX_VARS = 24;
const KEY_RE = /^[a-z_]{1,32}$/;

/** Strip control characters (including newlines) — a value is one line of text. */
function cleanValue(v: string): string {
  // eslint-disable-next-line no-control-regex -- deliberately stripping control chars
  return v
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .trim()
    .slice(0, MAX_VALUE_LEN);
}

/**
 * Turn an arbitrary (attribute JSON, query-string) source into a safe
 * key→value map: unknown shapes, non-string values, over-long keys, and keys
 * outside `[a-z_]` are dropped rather than throwing — a malformed `vars` is
 * "no personalisation" here, never a broken embed.
 */
export function sanitizeVars(input: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!input || typeof input !== 'object') return out;
  let n = 0;
  for (const [key, raw] of Object.entries(input as Record<string, unknown>)) {
    if (n >= MAX_VARS) break;
    if (key.length > MAX_KEY_LEN || !KEY_RE.test(key)) continue;
    if (typeof raw !== 'string') continue;
    const value = cleanValue(raw);
    if (!value) continue;
    out[key] = value;
    n++;
  }
  return out;
}

/** Parse a `vars` attribute/query value that arrives as a JSON string. */
export function parseVarsJson(raw: string | null | undefined): Record<string, string> {
  if (!raw) return {};
  try {
    return sanitizeVars(JSON.parse(raw));
  } catch {
    return {};
  }
}
